// Ход работы — карточкой прямо в ленте. SMY-72, переселено в SMY-114
//
// Задача карточки — превратить ожидание в доказательство работы. Полоса
// загрузки этого не делает: она показывает, что процесс идёт, но не что
// именно происходит. Этапы со своими состояниями и живой список источников
// показывают.
//
// В ленте, а не отдельным экраном: ход работы — часть хронологии одного
// вопроса, и, закончившись, он должен остаться на своём месте между вопросом
// и результатом, а не смениться другим экраном.
//
// ⚠️ ЗДЕСЬ ТОЛЬКО ПРОЦЕСС. Ни текста ответа, ни карточки результата: они идут
// НИЖЕ, отдельной репликой ассистента. Пока ответ печатался внутри этой
// карточки, готовый результат появлялся дважды — сырым текстом в процессе и
// нормальным ответом под ним, — и второй выглядел повтором первого. Граница
// простая: карточка отвечает на «что происходит», ответ — на «что вышло».
//
// ⚠️ ОБЕЩАНИЕ ПРО ЗАКРЫТИЕ. В макете «Можно закрыть — пришлю уведомление», но
// веб уведомления не шлёт: решение SMY-67 — Web Push не подключаем.
// Формулировка описывает то, что веб действительно делает.
//
// ⚠️ И ДАЁТСЯ ОНО НЕ ГОСТЮ (SMY-351, 05.10.2026). Гость живёт во вкладке
// (`token-store.ts`): закрыл её — и к задаче не вернётся, хотя сама задача на
// сервере доработает. До этого гость читал «Можно закрыть вкладку — задача не
// прервётся», закрывал по подсказке и терял и вопрос, и ответ. Теперь ему
// говорят, чем это кончится, и предлагают почту — тем же экраном, что стена.
// Пропадает доступ, а не данные: «работа не сохранится», как на стене, было бы
// неправдой (`docs/legal/guest-account.md`).

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { againLabel, isTerminal, refusalWhy, retryCost, RUN_OUTCOME_COPY, type ScenarioId } from '@smyslika/core'
import type { RunState } from '@smyslika/api-client'

import { useActiveRun } from '../active-run.tsx'
import { useSession } from '../session.tsx'
import { Card, Divider, Sources, Step, Tile } from '../ui/index.ts'

/**
 * Чем кончилось — заголовком и объяснением.
 *
 * Исходы не объединяются: «Не получилось» без причины оставляет человека
 * гадать, повторять ли — у сбоя ответ «да», у отказа по содержанию «нет».
 * Тексты в ядре (`RUN_OUTCOME_COPY`) с 05.10.2026: те же слова пишет в ленту
 * сервер, когда ответа нет (SMY-352), и третья копия разошлась бы с двумя.
 */
const OUTCOME: Record<string, { title: string; why: string }> = RUN_OUTCOME_COPY

/**
 * Заголовок карточки прогресса — обещание в настоящем времени.
 *
 * У чата он общий на оба случая, хотя ответ бывает и с походом в интернет.
 * Заголовок объявляется ДО классификации — то есть до того, как известно,
 * будет ли поход, — и «Ищу источники» на «сколько будет 17 × 43» было бы
 * обещанием работы, которой не будет. Что поиск идёт, видно строкой этапа и
 * списком найденного под ней.
 */
const RUNNING_TITLE: Record<ScenarioId, string> = {
  chat: 'Отвечаю',
  write: 'Пишу текст',
  doc_review: 'Разбираю документ',
  doc_create: 'Собираю документ',
  image: 'Рисую изображение',
}

/**
 * Таймер с начала запуска. Только пока идёт работа.
 *
 * Законченный запуск таймера не показывает, и это исправление, а не упрощение.
 * Тик останавливался вместе с работой, но на экране оставалось последнее
 * значение — то, каким `now` был на последней секунде. У быстро оборвавшегося
 * запуска это «0:01» рядом со строкой «Выполнилось за 19 с»: два разных числа
 * про одно и то же, и оба на виду (найдено на проде 06.09.2026).
 *
 * Настоящую длительность называет серая строка над ответом — она считает по
 * серверу. Второму числу здесь взяться неоткуда: снимок запуска `finishedAt`
 * не приносит.
 */
function useElapsed(startedAt: string | undefined, running: boolean): string | null {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!running) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [running])

  if (startedAt == null || !running) return null
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

export function RunCard({
  run,
  free,
  onRetry,
  retrying,
}: {
  run: RunState
  free: boolean
  /**
   * Повторить сбой тем же входом (SMY-352). Нет — повторять нечем: правка
   * результата заведена мимо `start`, а у запуска прошлой вкладки входа нет.
   */
  onRetry?: () => void
  /** Повтор уже ушёл: кнопка закрыта до ответа, второго запуска не будет. */
  retrying?: boolean
}) {
  const { cancel } = useActiveRun()
  const { guest } = useSession()
  const navigate = useNavigate()
  const [cancelling, setCancelling] = useState(false)

  const running = run.status === 'running'
  const finished = isTerminal(run.status)
  const elapsed = useElapsed(run.startedAt, running)

  // Успех объяснять нечем: результат человек видит сам. Отмена приходит
  // статусом, остальные исходы — полем outcome.
  const generic = finished
    ? (OUTCOME[run.status === 'cancelled' ? 'cancelled' : (run.outcome ?? 'done')] ?? null)
    : null

  // Отказ нашим правилом называет причину точнее общей строки: «откровенный
  // контент — не ко мне» вместо «на запрещённые темы не отвечаю» (SMY-78).
  // Про списание при этом сказать надо всё равно — это главное, что человеку
  // важно услышать после отказа, и молчание об этом он прочтёт как «списали».
  const ended =
    run.refusal != null
      ? {
          title: OUTCOME['blocked']!.title,
          why: refusalWhy(run.refusal),
        }
      : generic

  // Полоса активного этапа показывает долю ПРОЙДЕННЫХ этапов, а не ход
  // текущего: своего прогресса этап не сообщает, и рисовать проценты на глаз
  // значило бы придумать число, которого никто не считал.
  const passed = run.steps.length === 0 ? 0 : run.steps.filter((s) => s.status === 'done').length / run.steps.length

  return (
    <Card className={`run__card${finished ? ' run__card--quiet' : ''}`}>
      <div className="run__head">
        <span className="run__title">{finished ? (ended?.title ?? 'Готово') : RUNNING_TITLE[run.scenario]}</span>
        {running ? (
          <span className="run__badge">
            <span className="run__badge-dot" />
            Идёт работа
          </span>
        ) : null}
        {elapsed != null ? <span className="run__timer">{elapsed}</span> : null}
      </div>

      {/* Причина стоит над этапами, а не под ними: она отвечает на первый же
          вопрос — «что теперь делать», — и искать её под списком выполненного
          человек не станет. */}
      {ended != null ? (
        <p className="run__why" role="status">
          {ended.why}
        </p>
      ) : null}

      {/* Этапы остаются и после конца: законченный процесс — это и есть то,
          ради чего карточка стоит в ленте. Свернуть его до строки значило бы
          спрятать доказательство работы сразу после того, как оно появилось. */}
      {run.steps.length > 0 ? (
        <div className="run__steps">
          {run.steps.map((s) => (
            <Step
              key={s.id}
              status={s.status}
              label={s.label}
              {...(s.doneLabel != null ? { doneLabel: s.doneLabel } : {})}
              {...(s.detail != null ? { detail: s.detail } : {})}
              {...(s.status === 'active' ? { progress: passed } : {})}
            />
          ))}
        </div>
      ) : (
        <p className="muted run__waiting">Начинаю…</p>
      )}

      {/* Источники по мере находки — здесь же, а не в колонке сбоку: это и
          есть доказательство работы, ради которого на карточку и смотрят. */}
      {run.sources.length > 0 ? <Sources sources={run.sources} title="Нашёл" /> : null}
      {running && run.sources.length > 0 ? (
        <div className="sources__row sources__row--pending">
          <Tile size={22} color="var(--surface-alt)" />
          <span className="sources__title">Ищу дальше</span>
        </div>
      ) : null}

      {running ? (
        <>
          <Divider />
          <div className="run__foot">
            {/* Формулировка из SMY-67: веб уведомлений не шлёт, и обещать их
                нельзя. Зато запуск действительно переживает и вкладку, и
                перезагрузку — это правда, а не утешение. Гостю — правда другая
                (шапка, SMY-351): кнопкой, а не ссылкой — ссылку открывают
                Cmd-кликом в новой вкладке, а там уже другой гость, и почта
                легла бы не на его работу. Перезагрузку гостю не запрещаем: её
                вкладка переживает. В строке подсказки, а не рядом с
                «Отменить»: вторая обрывает работу, и две кнопки рядом — промах
                пальцем. Вид — общий у кнопок-ссылок (`.signin__link`), с его
                зоной нажатия 44px на узком экране. */}
            {guest ? (
              <span className="run__hint">
                Закроешь вкладку — без почты к задаче не вернёшься.{' '}
                <button type="button" className="signin__link" onClick={() => void navigate('/login')}>
                  Ввести почту
                </button>
              </span>
            ) : (
              <span className="run__hint">Можно закрыть вкладку — задача не прервётся.</span>
            )}
            <button
              type="button"
              className="run__cancel"
              disabled={cancelling}
              onClick={() => {
                setCancelling(true)
                void cancel().finally(() => setCancelling(false))
              }}
            >
              Отменить
            </button>
          </div>
        </>
      ) : run.outcome === 'failed' && onRetry != null ? (
        // Повтор сбоя — прямо отсюда, тем же входом (SMY-352). Окна
        // подтверждения нет: человек подтвердил ровно эту задачу, а повтор
        // стоит как первая попытка — сбой запас не тратит (`docs/billing.md`).
        // Строка рядом — про следующий запуск, объяснение выше — про этот.
        <>
          <Divider />
          <div className="run__foot">
            <span className="run__hint">
              {retryCost(free, run.unit)}
            </span>
            <button type="button" className="btn btn--primary" disabled={retrying === true} onClick={onRetry}>
              {againLabel(run.scenario)}
            </button>
          </div>
        </>
      ) : run.artifact == null && (run.charged || free || ended == null) ? (
        // Про списание говорит тот, кто показывает результат. У готовой
        // карточки строка своя, и повторить её здесь значило бы сказать одно и
        // то же дважды подряд. У платного сбоя, отказа и отмены подвала нет
        // (SMY-361): объяснение выше уже сказало и про повтор, и про списание, а
        // прежнее «Задача закончилась.» под «Попробуй запустить ещё раз»
        // читалось как «повторять нечего». У бесплатного строка остаётся:
        // «Уточнение — без списания лимита.» объяснение не повторяет.
        <p className="run__hint">
          {run.charged
            ? 'Единица лимита списана за готовый результат.'
            : free
              ? 'Уточнение — без списания лимита.'
              : 'С лимита ничего не списалось.'}
        </p>
      ) : null}
    </Card>
  )
}
